import React, { Suspense, useCallback, useEffect, useState } from 'react'
import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import CircularProgress from '@material-ui/core/CircularProgress'
import type { QuoteFile } from 'paintscout'

export interface FileEditorSavePayload {
  /** The freshly-edited image as a Blob, ready to be uploaded. */
  blob: Blob
  /** Suggested filename. Useful when constructing a File for the upload pipeline. */
  fileName: string
  mimeType: string
  width?: number
  height?: number
  /**
   * Snapshot of the editor's state (crop/markup/adjustments) so the edit can be
   * re-opened non-destructively. The caller persists this onto the QuoteFile
   * (see `mergeEditedFile`). Undefined when the editor can't produce one.
   */
  editState?: QuoteFile['imageEditState']
}

export type FileViewerEditorFile = QuoteFile & { error?: string }

export interface FileViewerEditorProps {
  file: FileViewerEditorFile
  onBack: () => void
  /**
   * Invoked when the user saves. The caller is responsible for uploading the
   * resulting Blob and updating the QuoteFile (e.g. stashing originalSrc).
   */
  onSave: (payload: FileEditorSavePayload) => Promise<void> | void
}

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    height: '100%',
    position: 'relative',
    background: theme.palette.common.white,
    overflow: 'hidden'
  },
  loading: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  variantPill: {
    position: 'absolute',
    bottom: theme.spacing(1.25),
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 10,
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    background: theme.palette.common.white,
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    borderRadius: 999,
    padding: theme.spacing(0.25, 1),
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    fontSize: 11,
    color: theme.paintscout.palette.brand.Neutral.P50,
    pointerEvents: 'auto'
  },
  variantBtn: {
    background: 'transparent',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    color: theme.palette.primary.main,
    fontSize: 11,
    fontWeight: theme.typography.fontWeightMedium,
    '&:hover': {
      textDecoration: 'underline'
    }
  }
}))

// ─── EXPERIMENT: editor-variant A/B toggle ────────────────────────────────────
// Two image editors live side-by-side for evaluation: react-filerobot-image-editor
// and our konva-based implementation. The choice persists in localStorage and
// a subtle dev pill in the bottom-right of the editor toggles between them.
// When we pick a winner, delete this fence, the variant state + pill below,
// and `KonvaInner.tsx` or `FilerobotInner.tsx` for whichever loses.
// ─────────────────────────────────────────────────────────────────────────────
export type FileEditorVariant = 'filerobot' | 'konva'
export const EDITOR_VARIANT_STORAGE_KEY = 'paintscout:fileViewer:editor'
const DEFAULT_VARIANT: FileEditorVariant = 'konva'

// Flip to `true` to expose the konva/filerobot toggle pill again.
const SHOW_VARIANT_PILL = false

function readStoredVariant(): FileEditorVariant {
  if (typeof window === 'undefined') return DEFAULT_VARIANT
  try {
    const raw = window.localStorage.getItem(EDITOR_VARIANT_STORAGE_KEY)
    return raw === 'konva' || raw === 'filerobot' ? raw : DEFAULT_VARIANT
  } catch {
    return DEFAULT_VARIANT
  }
}

const FilerobotInner = React.lazy(() => import('./FilerobotInner'))
const KonvaInner = React.lazy(() => import('./KonvaInner'))

function FileViewerEditor(props: FileViewerEditorProps) {
  const { file, onBack, onSave } = props
  const classes = useStyles({})

  const [variant, setVariant] = useState<FileEditorVariant>(() => readStoredVariant())

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === EDITOR_VARIANT_STORAGE_KEY && (e.newValue === 'konva' || e.newValue === 'filerobot')) {
        setVariant(e.newValue)
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const toggleVariant = useCallback(() => {
    setVariant((prev) => {
      const next: FileEditorVariant = prev === 'filerobot' ? 'konva' : 'filerobot'
      try {
        window.localStorage.setItem(EDITOR_VARIANT_STORAGE_KEY, next)
      } catch {
        /* localStorage may be disabled */
      }
      return next
    })
  }, [])

  return (
    <div className={classes.root}>
      <Suspense
        fallback={
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        }
      >
        {variant === 'filerobot' ? (
          <FilerobotInner key="filerobot" file={file} onClose={onBack} onSave={onSave} />
        ) : (
          <KonvaInner key="konva" file={file} onClose={onBack} onSave={onSave} />
        )}
      </Suspense>
      {/* EXPERIMENT pill — hidden for now. Flip SHOW_VARIANT_PILL above to true
          to re-enable the konva/filerobot toggle without re-wiring anything. */}
      {SHOW_VARIANT_PILL && (
        <div className={classes.variantPill} title="A/B editor toggle (dev only)">
          <span>editor: {variant}</span>
          <button type="button" className={classes.variantBtn} onClick={toggleVariant}>
            switch
          </button>
        </div>
      )}
    </div>
  )
}

export default FileViewerEditor
